import { format } from 'date-fns';
import { parsePrice } from './priceUtils';
import { DELIVERY_TYPES } from '../config/availability';

/**
 * Determines whether a booking requires manual approval before confirmation.
 *
 * @param {boolean} outsideAvailabilityRequested
 * @param {boolean} customTimeRequested
 * @param {boolean|null|undefined} autoApproval - bookingPage.autoApproval. false = require approval; null/undefined/true = auto-approve.
 * @returns {boolean}
 */
export const requiresApproval = (outsideAvailabilityRequested, customTimeRequested, autoApproval) => {
  return outsideAvailabilityRequested || customTimeRequested || autoApproval === false;
};

/**
 * Calculates service totals, travel fee, and combined total for a booking.
 *
 * @param {Array} selectedServices - Array of { service, quantity } objects
 * @param {string|null} selectedAppointmentType - 'mobile' | 'fixed' | null
 * @param {boolean} travelFeesEnabled - bookingPage.travelFeesEnabled
 * @param {number|null} travelFee - Calculated travel fee amount
 * @returns {{ serviceTotal: number, totalPrice: number, totalItems: number, applicableTravelFee: number }}
 */
export const calculateBookingTotals = (selectedServices, selectedAppointmentType, travelFeesEnabled, travelFee) => {
  let serviceTotal = 0;
  let totalItems = 0;

  selectedServices.forEach(({ service, quantity }) => {
    const numericPrice = parsePrice(service.price);
    serviceTotal += numericPrice * quantity;
    totalItems += quantity;
  });

  const applicableTravelFee =
    selectedAppointmentType === 'mobile' &&
    travelFeesEnabled &&
    travelFee !== null &&
    travelFee > 0 ? travelFee : 0;

  const totalPrice = serviceTotal + applicableTravelFee;

  return { serviceTotal, totalPrice, totalItems, applicableTravelFee };
};

/**
 * Returns the total duration (minutes) for all selected services.
 *
 * @param {Array} selectedServices - Array of { service, quantity } objects
 * @returns {number}
 */
export const getTotalServiceDuration = (selectedServices) => {
  return selectedServices.reduce((total, item) => {
    return total + item.service.duration * item.quantity;
  }, 0);
};

/**
 * Determines whether the appointment-setup step is complete enough to proceed.
 *
 * @param {string|null} selectedAppointmentType - 'mobile' | 'fixed' | null
 * @param {Object|null} selectedLocation - Selected fixed location
 * @param {string|null} selectedTime - Selected time string
 * @param {Object|null} selectedAddress - Selected mobile address
 * @param {number} fixedLocationsCount - Number of fixed locations available (from fixedLocationData.locations.length)
 * @returns {boolean}
 */
export const isAppointmentSetupComplete = (
  selectedAppointmentType,
  selectedLocation,
  selectedTime,
  selectedAddress,
  fixedLocationsCount
) => {
  if (selectedAppointmentType === 'fixed') {
    const needsLocationSelection = fixedLocationsCount > 1;
    const hasRequiredLocation = needsLocationSelection ? selectedLocation !== null : true;
    return hasRequiredLocation && selectedTime !== null;
  }
  if (selectedAppointmentType === 'mobile') {
    return selectedAddress !== null && selectedTime !== null;
  }
  return false;
};

/**
 * Determines whether the continue/submit button should be disabled.
 *
 * @param {string} currentBookingStep - 'services' | 'appointment-setup' | 'contact-confirmation'
 * @param {boolean} isSetupComplete - Pre-computed result of isAppointmentSetupComplete
 * @param {boolean} isMutationPending - Whether the create-appointment mutation is in flight
 * @returns {boolean}
 */
export const shouldDisableContinueButton = (currentBookingStep, isSetupComplete, isMutationPending, hasSelectedServices = true) => {
  if (currentBookingStep === 'services') {
    return !hasSelectedServices;
  }
  if (currentBookingStep === 'appointment-setup') {
    return !isSetupComplete;
  }
  if (currentBookingStep === 'contact-confirmation') {
    return isMutationPending;
  }
  return false;
};

/**
 * Returns the label for the back-navigation button.
 *
 * Intentionally terse — the stepper already shows the previous step name,
 * so the back button only needs the affordance word, not the destination.
 *
 * @param {string} currentBookingStep
 * @returns {string}
 */
export const getBackButtonText = (_currentBookingStep) => {
  return 'Back';
};

/**
 * Returns the label for the continue/submit button.
 *
 * @param {string} currentBookingStep
 * @param {boolean} requiresApprovalFlag - Pre-computed result of requiresApproval
 * @param {boolean} [isSubmitting=false] - true while the create-appointment mutation is in-flight
 * @returns {string}
 */
export const getContinueButtonText = (currentBookingStep, requiresApprovalFlag, isSubmitting = false) => {
  if (currentBookingStep === 'contact-confirmation') {
    if (isSubmitting) {
      return requiresApprovalFlag ? 'Submitting request…' : 'Completing booking…';
    }
    return requiresApprovalFlag ? 'Submit Request' : 'Complete Booking';
  }
  if (currentBookingStep === 'services') {
    return 'Choose Date & Time';
  }
  if (currentBookingStep === 'appointment-setup') {
    return 'Next: Your Details';
  }
  return 'Continue to Booking';
};

/**
 * Returns a human-readable label for an appointment type.
 *
 * @param {string|null} appointmentType - 'mobile' | 'fixed' | null
 * @returns {string}
 */
export const getServiceTypeLabel = (appointmentType) => {
  if (appointmentType === 'mobile') return 'We come to you';
  if (appointmentType === 'fixed') return 'Visit in person';
  return 'Not selected';
};

/**
 * Builds the appointment data object sent to createPublicAppointment.
 *
 * @param {Object} contactFormData
 * @param {Array} selectedServices
 * @param {string|null} selectedAppointmentType
 * @param {Object|null} selectedAddress
 * @param {Object|null} selectedLocation
 * @param {Date|string|null} selectedDate - Day of the appointment (without time-of-day)
 * @param {string|null} selectedTime - Time-of-day "HH:MM"; combined with selectedDate here to preserve the existing wire format
 * @param {number|null} travelFee
 * @param {boolean} outsideAvailabilityRequested
 * @param {boolean} customTimeRequested
 * @returns {Object}
 */
export const buildAppointmentPayload = (
  contactFormData,
  selectedServices,
  selectedAppointmentType,
  selectedAddress,
  selectedLocation,
  selectedDate,
  selectedTime,
  travelFee,
  outsideAvailabilityRequested,
  customTimeRequested
) => {
  const combinedDate =
    selectedDate instanceof Date && selectedTime
      ? new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}:00`)
      : selectedDate;

  return {
    contactFormData,
    selectedServices,
    selectedAppointmentType,
    selectedAddress,
    selectedLocation,
    selectedDate: combinedDate,
    travelFee,
    outsideAvailabilityRequested,
    customTimeRequested,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 * Derives the combined delivery modes available for the current cart, then
 * narrows the output against the provider-wide availability summary.
 *
 * Two concerns are kept strictly separate:
 *
 *   `conflict`   — SERVICE-LEVEL ONLY. True only when the services in the cart
 *                  are mutually incompatible (e.g. a mobile-only service and a
 *                  fixed-only service together). A provider capability gap (e.g.
 *                  a mobile-only service but the provider has no mobile
 *                  availability) is NOT a conflict — the booking degrades
 *                  gracefully to no-slots / outside-area downstream (spec §3.4).
 *
 *   `hasMobile`  — service-level intersection AND provider has mobile.
 *   `hasFixed`   — service-level intersection AND provider has fixed.
 *
 * Cart items use the shape { service, quantity } — the same shape produced by
 * usePublicBookingFlow.updateServiceQuantity. A missing or unrecognised
 * service.deliveryType is treated as "hybrid" for back-compat with existing
 * services that pre-date the per-service field.
 *
 * Empty cart: falls back to the provider-wide modes so the appointment-setup
 * step behaves exactly as before per-service delivery types existed
 * (no conflict, provider-wide modes unchanged).
 *
 * @param {Array<{service: {deliveryType?: string}, quantity: number}>} selectedServices
 * @param {{hasFixed: boolean, hasMobile: boolean}} serviceLocationTypes - provider-wide summary
 * @returns {{ hasFixed: boolean, hasMobile: boolean, conflict: boolean }}
 */
export const deriveCartDeliveryModes = (selectedServices, serviceLocationTypes) => {
  const providerHasFixed = serviceLocationTypes?.hasFixed ?? false;
  const providerHasMobile = serviceLocationTypes?.hasMobile ?? false;

  // Empty cart: no narrowing, fall back to provider-wide modes, no conflict.
  if (!selectedServices || selectedServices.length === 0) {
    return { hasFixed: providerHasFixed, hasMobile: providerHasMobile, conflict: false };
  }

  // Intersect service capability sets across all cart items.
  // Start with both modes true; each service can only remove capabilities.
  let intersectMobile = true;
  let intersectFixed = true;

  for (const item of selectedServices) {
    const deliveryType = item?.service?.deliveryType ?? DELIVERY_TYPES.HYBRID;

    if (deliveryType === DELIVERY_TYPES.MOBILE) {
      intersectFixed = false;
    } else if (deliveryType === DELIVERY_TYPES.FIXED) {
      intersectMobile = false;
    }
    // "hybrid" removes nothing from the intersection.

    // Short-circuit: once both are false the services are mutually incompatible.
    if (!intersectMobile && !intersectFixed) break;
  }

  // `conflict` reflects only service-level incompatibility — it is independent
  // of provider capability. A single mobile-only service with a fixed-only
  // provider is NOT a conflict; it simply yields no available slots downstream.
  const conflict = !intersectMobile && !intersectFixed;

  // `hasFixed`/`hasMobile` are narrowed by the provider's actual availability.
  // These drive address-input gating and auto-selection in the booking flow.
  return {
    hasFixed: intersectFixed && providerHasFixed,
    hasMobile: intersectMobile && providerHasMobile,
    conflict,
  };
};

/**
 * Resolves the EFFECTIVE delivery mode a customer can actually book for a single
 * service, narrowed by the provider's real availability. This is what a
 * customer-facing delivery badge must reflect — NOT the raw service.deliveryType.
 *
 * Reuses deriveCartDeliveryModes (single source of truth for narrowing) by
 * treating the service as a one-item cart. Examples:
 *   - hybrid service + provider has only fixed availability  → "fixed"
 *     (offering mobile would yield zero slots — the badge must not promise it)
 *   - hybrid service + provider has only mobile              → "mobile"
 *   - hybrid service + provider has both                     → "hybrid"
 *   - mobile-only service + provider has only fixed          → null (not bookable)
 *
 * @param {{deliveryType?: string}} service
 * @param {{hasFixed: boolean, hasMobile: boolean}} serviceLocationTypes - provider-wide summary
 * @returns {"mobile" | "fixed" | "hybrid" | null} null when the service is not
 *          currently bookable in any mode (provider lacks the needed availability).
 */
export const getEffectiveDeliveryMode = (service, serviceLocationTypes) => {
  const { hasMobile, hasFixed } = deriveCartDeliveryModes(
    [{ service }],
    serviceLocationTypes
  );

  if (hasMobile && hasFixed) return DELIVERY_TYPES.HYBRID;
  if (hasMobile) return DELIVERY_TYPES.MOBILE;
  if (hasFixed) return DELIVERY_TYPES.FIXED;
  return null;
};
